import { type FormEvent, useRef, useState } from "react";
import { getPublicSupabaseClient } from "./lib/supabase";

type PaymentPolicy = "free" | "online_required" | "invoice_required" | "online_or_invoice";
type PaymentChoice = "online" | "invoice" | "cash" | "not_required";

type RegistrationRole = {
  id: string;
  label: string;
  price_cents: number | null;
  currency: string;
  payment_policy: PaymentPolicy | string;
};

type RegistrationResult = {
  registration_id: string;
  reference: string;
  registration_status: "pending" | "confirmed" | "waitlist";
  payment_status: string;
  amount_cents: number | null;
  currency: string;
  payment_policy: string;
  payment_choice: PaymentChoice;
  payment_due_at: string | null;
  original_amount_cents?: number | null;
  discount_cents?: number;
  booking_code_label?: string | null;
};

type InvitationPreview = {
  valid: boolean;
  label: string | null;
  original_amount_cents: number | null;
  discount_cents: number;
  amount_cents: number | null;
  currency: string;
  payment_required: boolean;
  message: string;
};

type EventRegistrationFormProps = {
  eventId: string;
  eventTitle: string;
  roles: RegistrationRole[];
  isLive: boolean;
  initialRoleId?: string | null;
  showTrigger?: boolean;
  allowCashPayment?: boolean;
  returnUrl?: string;
  legalBaseUrl?: string;
};

function formatPrice(priceCents: number | null, currency: string, paymentPolicy?: string) {
  if (paymentPolicy === "free" || priceCents === 0) return "Beitrag offen";
  if (priceCents === null) return "Preis folgt";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(priceCents / 100);
}

function formatDueAt(value: string | null) {
  if (!value) return null;
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}

function rolePaymentChoice(role: RegistrationRole | undefined): PaymentChoice {
  if (!role || (role.price_cents ?? 0) <= 0 || role.payment_policy === "free")
    return "not_required";
  if (role.payment_policy === "invoice_required") return "invoice";
  return "online";
}

function canStartCheckout(registration: RegistrationResult) {
  return (
    registration.registration_status === "pending" &&
    registration.payment_status !== "paid" &&
    registration.payment_choice === "online" &&
    registration.amount_cents !== null &&
    registration.amount_cents > 0 &&
    ["online_required", "online_or_invoice"].includes(registration.payment_policy)
  );
}

function contributionCents(value: string) {
  const normalized = value.trim().replace(",", ".");
  if (!normalized) return 0;
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.round(amount * 100);
}

async function checkoutErrorDetails(
  error: unknown,
): Promise<{ message: string | null; status: number | null }> {
  const context =
    typeof error === "object" && error !== null && "context" in error
      ? (error as { context?: unknown }).context
      : null;

  if (context && typeof context === "object" && "status" in context) {
    const response = context as Response;
    let message: string | null = null;
    try {
      const payload = (await response.clone().json()) as { error?: unknown };
      if (typeof payload.error === "string" && payload.error.trim()) {
        message = payload.error.trim();
      }
    } catch {
      // Keep the transport fallback below when the response is not JSON.
    }
    return { message, status: Number.isFinite(response.status) ? response.status : null };
  }

  return {
    message:
      error instanceof Error && error.message && !error.message.toLowerCase().includes("non-2xx")
        ? error.message
        : null,
    status: null,
  };
}

export function EventRegistrationForm({
  eventId,
  eventTitle,
  roles,
  isLive,
  initialRoleId = null,
  showTrigger = true,
  allowCashPayment = false,
  returnUrl,
  legalBaseUrl = "",
}: EventRegistrationFormProps) {
  const submitting = useRef(false);
  const initialRole = roles.find((role) => role.id === initialRoleId) ?? roles[0];
  const [isOpen, setIsOpen] = useState(Boolean(initialRoleId));
  const [selectedRoleId, setSelectedRoleId] = useState(initialRole?.id ?? "");
  const [paymentChoice, setPaymentChoice] = useState<PaymentChoice>(rolePaymentChoice(initialRole));
  const [invitationCode, setInvitationCode] = useState("");
  const [codePreview, setCodePreview] = useState<InvitationPreview | null>(null);
  const [contributionAmount, setContributionAmount] = useState("");
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpeningCheckout, setIsOpeningCheckout] = useState(false);
  const [isOpeningContribution, setIsOpeningContribution] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  const [result, setResult] = useState<RegistrationResult | null>(null);
  const selectedRole = roles.find((role) => role.id === selectedRoleId) ?? roles[0];
  const effectiveAmount = codePreview?.valid ? codePreview.amount_cents : selectedRole?.price_cents;
  const showPaymentChoice =
    selectedRole?.payment_policy === "online_or_invoice" && (effectiveAmount ?? 0) > 0;
  const contributionEnabled = Boolean(codePreview?.valid && !codePreview.payment_required);

  function selectRole(role: RegistrationRole) {
    setSelectedRoleId(role.id);
    setPaymentChoice(rolePaymentChoice(role));
    setCodePreview(null);
    setContributionAmount("");
  }

  async function checkInvitationCode(): Promise<InvitationPreview | null> {
    const code = invitationCode.trim();
    if (!code || !selectedRole) {
      setCodePreview(null);
      setContributionAmount("");
      return null;
    }
    setIsCheckingCode(true);
    setErrorMessage(null);
    try {
      const supabase = getPublicSupabaseClient();
      const { data, error } = await supabase.rpc("preview_event_invitation_code", {
        p_event_id: eventId,
        p_event_role_id: selectedRole.id,
        p_code: code,
      });
      if (error) throw error;
      const preview = (data as InvitationPreview[] | null)?.[0];
      if (!preview) throw new Error("Der Einladungscode konnte nicht geprüft werden.");
      setCodePreview(preview);
      if (preview.valid && !preview.payment_required) setPaymentChoice("not_required");
      else if (preview.valid) setPaymentChoice(rolePaymentChoice(selectedRole));
      if (!preview.valid || preview.payment_required) setContributionAmount("");
      return preview;
    } catch (error) {
      setCodePreview(null);
      setContributionAmount("");
      setErrorMessage(
        error instanceof Error ? error.message : "Der Einladungscode konnte nicht geprüft werden.",
      );
      return null;
    } finally {
      setIsCheckingCode(false);
    }
  }

  async function notifyReservation(registration: RegistrationResult) {
    try {
      const supabase = getPublicSupabaseClient();
      const { error } = await supabase.functions.invoke("send-event-registration-received", {
        body: { registrationId: registration.registration_id, reference: registration.reference },
      });
      if (error) throw error;
    } catch (error) {
      console.error("Reservation notification could not be sent.", error);
      setNoticeMessage(
        "Deine Anmeldung ist gespeichert. Die Bestätigungsmail kann sich gerade etwas verzögern.",
      );
    }
  }

  async function openCheckout(registration: RegistrationResult) {
    setErrorMessage(null);
    setIsOpeningCheckout(true);
    try {
      const supabase = getPublicSupabaseClient();
      let lastError: unknown;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        const { data: checkout, error: checkoutError } = await supabase.functions.invoke(
          "create-event-checkout",
          {
            body: {
              registrationId: registration.registration_id,
              reference: registration.reference,
              returnUrl,
            },
          },
        );

        if (!checkoutError && checkout?.checkoutUrl) {
          if (checkout.notificationWarning) {
            setNoticeMessage(String(checkout.notificationWarning));
          }
          window.location.assign(checkout.checkoutUrl);
          return;
        }

        const details = await checkoutErrorDetails(checkoutError);
        lastError = new Error(
          details.message ?? "Die sichere Zahlungsseite konnte gerade nicht geöffnet werden.",
        );

        if (details.status !== null && details.status < 500) {
          break;
        }
        if (attempt === 0) {
          await new Promise((resolve) => window.setTimeout(resolve, 450));
        }
      }

      throw lastError;
    } catch (error) {
      console.error("Event checkout handoff failed.", error);
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "Die sichere Zahlungsseite konnte gerade nicht geöffnet werden. Bitte versuche es erneut.",
      );
    } finally {
      setIsOpeningCheckout(false);
    }
  }

  async function openContributionCheckout(
    registration: RegistrationResult,
    amountValue = contributionAmount,
  ) {
    const cents = contributionCents(amountValue);
    if (cents <= 0) return;
    setErrorMessage(null);
    setIsOpeningContribution(true);
    try {
      const supabase = getPublicSupabaseClient();
      const { data: checkout, error: checkoutError } = await supabase.functions.invoke(
        "create-event-checkout",
        {
          body: {
            registrationId: registration.registration_id,
            reference: registration.reference,
            returnUrl,
            contributionAmountCents: cents,
          },
        },
      );
      if (checkoutError) throw checkoutError;
      if (!checkout?.checkoutUrl)
        throw new Error("Die Stripe-Seite für deinen Beitrag konnte nicht geöffnet werden.");
      window.location.assign(checkout.checkoutUrl);
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "Die Seite für deinen Beitrag konnte gerade nicht geöffnet werden. Bitte versuche es erneut.",
      );
    } finally {
      setIsOpeningContribution(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setErrorMessage(null);
    setNoticeMessage(null);
    setIsSubmitting(true);
    const form = new FormData(event.currentTarget);
    try {
      let activeCodePreview = codePreview;
      if (invitationCode.trim() && (!activeCodePreview || !activeCodePreview.valid)) {
        activeCodePreview = await checkInvitationCode();
        if (!activeCodePreview?.valid) return;
      }

      const supabase = getPublicSupabaseClient();
      const { data, error } = await supabase.rpc("submit_event_registration", {
        p_event_id: eventId,
        p_event_role_id: String(form.get("eventRoleId") ?? ""),
        p_first_name: String(form.get("firstName") ?? ""),
        p_last_name: String(form.get("lastName") ?? ""),
        p_email: String(form.get("email") ?? ""),
        p_phone: String(form.get("phone") ?? ""),
        p_note: String(form.get("note") ?? ""),
        p_terms_accepted: form.get("termsAccepted") === "on",
        p_payment_choice:
          activeCodePreview?.valid && !activeCodePreview.payment_required
            ? null
            : paymentChoice === "not_required"
              ? null
              : paymentChoice,
        p_invitation_code: invitationCode.trim() || null,
      });
      if (error) throw error;
      const registration = (data as RegistrationResult[] | null)?.[0];
      if (!registration) throw new Error("Die Anmeldung konnte nicht bestätigt werden.");
      setResult(registration);
      if (canStartCheckout(registration)) {
        await openCheckout(registration);
      } else {
        await notifyReservation(registration);
        if (
          registration.booking_code_label &&
          registration.payment_status === "not_required" &&
          contributionCents(contributionAmount) > 0
        ) {
          await openContributionCheckout(registration);
        }
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "Die Anmeldung konnte gerade nicht gespeichert werden. Bitte versuche es erneut.",
      );
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  }

  if (!isLive) {
    return (
      <div className="public-event__status" role="status" aria-label="Anmeldestatus">
        <span>Anmeldung vorübergehend nicht verfügbar</span>
        <small>Die Veranstaltungsdaten konnten gerade nicht sicher geladen werden.</small>
      </div>
    );
  }

  if (result) {
    const checkoutAvailable = canStartCheckout(result);
    const dueAt = formatDueAt(result.payment_due_at);
    const contributionRegistration = Boolean(
      result.booking_code_label && result.payment_status === "not_required",
    );
    const resultText =
      result.registration_status === "waitlist"
        ? "Du stehst zunächst auf der Warteliste. Eine Zahlung ist noch nicht erforderlich."
        : contributionRegistration
          ? "Dein Platz ist bestätigt. Deinen Beitrag kannst du frei wählen."
          : result.payment_status === "not_required"
            ? "Dein Platz ist bestätigt. Du musst nichts weiter tun."
            : result.payment_choice === "cash"
              ? "Dein Platz ist bestätigt. Du bezahlst den Betrag vor Ort in bar."
              : result.payment_choice === "invoice"
                ? `Dein Platz ist reserviert${dueAt ? ` bis ${dueAt}` : ""}. Die Rechnung erhältst du separat.`
                : `Dein Platz ist reserviert${dueAt ? ` bis ${dueAt}` : ""}. Jetzt fehlt nur noch die Onlinezahlung.`;

    return (
      <div className="event-registration__success" role="status">
        <p>
          {result.registration_status === "waitlist"
            ? "Du bist auf der Warteliste."
            : result.payment_status === "not_required"
              ? "Du bist dabei."
              : result.payment_choice === "cash"
                ? "Du bist dabei."
                : result.payment_choice === "invoice"
                  ? "Dein Platz ist reserviert."
                  : "Fast geschafft."}
        </p>
        <strong>{eventTitle}</strong>
        <span>{resultText}</span>
        {result.booking_code_label ? <small>Einladung: {result.booking_code_label}</small> : null}
        {result.amount_cents !== null && result.amount_cents > 0 ? (
          <small>Betrag: {formatPrice(result.amount_cents, result.currency)}</small>
        ) : null}
        <small>Referenz: {result.reference}</small>

        {contributionRegistration ? (
          <div className="event-registration__contribution">
            <div>
              <strong>Freiwilliger Beitrag</strong>
              <span>
                Du entscheidest selbst, was du für diesen Tag geben möchtest. Auch 0 € ist möglich –
                dein Platz ist bereits bestätigt.
              </span>
            </div>
            <label>
              Betrag in €
              <input
                type="number"
                inputMode="decimal"
                min="0"
                max="10000"
                step="1"
                placeholder="z. B. 50"
                value={contributionAmount}
                onChange={(event) => setContributionAmount(event.target.value)}
              />
            </label>
            <button
              className="event-registration__contribution-button"
              disabled={isOpeningContribution || contributionCents(contributionAmount) <= 0}
              type="button"
              onClick={() => void openContributionCheckout(result)}
            >
              {isOpeningContribution ? "Stripe wird geöffnet …" : "Beitrag über Stripe geben"}
            </button>
            <small>Wenn du nichts geben möchtest, ist nichts weiter nötig.</small>
          </div>
        ) : null}

        {noticeMessage ? <p className="event-registration__notice">{noticeMessage}</p> : null}
        {errorMessage ? (
          <p className="event-registration__error" role="alert">
            {errorMessage}
          </p>
        ) : null}
        {checkoutAvailable ? (
          <button
            className="event-registration__open"
            disabled={isOpeningCheckout}
            type="button"
            onClick={() => void openCheckout(result)}
          >
            {isOpeningCheckout ? "Zahlungsseite wird geöffnet …" : "Jetzt sicher online bezahlen"}
          </button>
        ) : null}
      </div>
    );
  }

  if (!isOpen && showTrigger) {
    return (
      <button className="event-registration__open" type="button" onClick={() => setIsOpen(true)}>
        Jetzt anmelden
      </button>
    );
  }

  return (
    <form className="event-registration" onSubmit={handleSubmit}>
      <div className="event-registration__heading">
        <span>Dein Platz</span>
        <h4>{eventTitle}</h4>
        <p>
          Teilnahme wählen, Kontaktdaten eintragen, Zahlungsweg festlegen. Danach ist klar, woran du
          bist.
        </p>
      </div>

      <fieldset>
        <legend>Wie möchtest du teilnehmen?</legend>
        <div className="event-registration__role-options">
          {roles.map((role) => (
            <label key={role.id}>
              <input
                checked={selectedRoleId === role.id}
                name="eventRoleId"
                required
                type="radio"
                value={role.id}
                onChange={() => selectRole(role)}
              />
              <span>
                <strong>{role.label}</strong>
                <small>
                  {formatPrice(role.price_cents, role.currency, role.payment_policy)}
                  {role.payment_policy === "free" || !role.price_cents ? "" : " inkl. MwSt."}
                </small>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {(selectedRole?.price_cents ?? 0) > 0 ? (
        <div className="event-registration__invitation-code">
          <label htmlFor={`invitation-code-${eventId}`}>
            Einladungscode <small>optional</small>
          </label>
          <div>
            <input
              id={`invitation-code-${eventId}`}
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={80}
              value={invitationCode}
              onChange={(event) => {
                setInvitationCode(event.target.value);
                setCodePreview(null);
                setContributionAmount("");
                if (paymentChoice === "not_required")
                  setPaymentChoice(rolePaymentChoice(selectedRole));
              }}
              placeholder="Code eingeben"
            />
            <button
              type="button"
              disabled={isCheckingCode || !invitationCode.trim()}
              onClick={() => void checkInvitationCode()}
            >
              {isCheckingCode ? "Prüfen …" : "Code einlösen"}
            </button>
          </div>
          {codePreview ? (
            <p className={codePreview.valid ? "is-valid" : "is-invalid"} role="status">
              {codePreview.message}
              {codePreview.valid &&
              codePreview.original_amount_cents !== codePreview.amount_cents ? (
                <span>
                  <s>{formatPrice(codePreview.original_amount_cents, codePreview.currency)}</s>{" "}
                  <strong>{formatPrice(codePreview.amount_cents, codePreview.currency)}</strong>
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}

      {contributionEnabled ? (
        <div className="event-registration__contribution event-registration__contribution--before-submit">
          <div>
            <strong>Dein Beitrag ist frei wählbar</strong>
            <span>Trag ein, was du für den Tag geben möchtest. 0 € ist ebenfalls möglich.</span>
          </div>
          <label>
            Betrag in €
            <input
              type="number"
              inputMode="decimal"
              min="0"
              max="10000"
              step="1"
              placeholder="z. B. 50"
              value={contributionAmount}
              onChange={(event) => setContributionAmount(event.target.value)}
            />
          </label>
        </div>
      ) : null}

      {showPaymentChoice ? (
        <fieldset className="event-registration__payment-choice">
          <legend>Wie möchtest du bezahlen?</legend>
          <div>
            <label>
              <input
                checked={paymentChoice === "online"}
                name="paymentChoice"
                type="radio"
                value="online"
                onChange={() => setPaymentChoice("online")}
              />
              <span>
                <strong>Direkt online bezahlen</strong>
                <small>Sicher über Stripe. Deine Rechnung erhältst du natürlich trotzdem.</small>
              </span>
            </label>
            <label>
              <input
                checked={paymentChoice === "invoice"}
                name="paymentChoice"
                type="radio"
                value="invoice"
                onChange={() => setPaymentChoice("invoice")}
              />
              <span>
                <strong>Per Rechnung bezahlen</strong>
                <small>
                  Dein Platz wird reserviert. Die Rechnung erhältst du separat und zahlst
                  anschließend per Überweisung.
                </small>
              </span>
            </label>
            {allowCashPayment ? (
              <label>
                <input
                  checked={paymentChoice === "cash"}
                  name="paymentChoice"
                  type="radio"
                  value="cash"
                  onChange={() => setPaymentChoice("cash")}
                />
                <span>
                  <strong>Vor Ort bar bezahlen</strong>
                  <small>
                    Dein Platz ist verbindlich bestätigt. Du bezahlst den Betrag beim Intensive in
                    bar.
                  </small>
                </span>
              </label>
            ) : null}
          </div>
        </fieldset>
      ) : null}

      <div className="event-registration__grid">
        <label>
          Vorname
          <input autoComplete="given-name" maxLength={80} name="firstName" required />
        </label>
        <label>
          Nachname
          <input autoComplete="family-name" maxLength={80} name="lastName" required />
        </label>
        <label>
          E-Mail
          <input autoComplete="email" maxLength={254} name="email" required type="email" />
        </label>
        <label>
          Telefon <small>optional</small>
          <input autoComplete="tel" maxLength={50} name="phone" type="tel" />
        </label>
      </div>

      <label>
        Was sollte ich vorher wissen? <small>optional</small>
        <textarea maxLength={2000} name="note" rows={3} />
      </label>

      <label className="event-registration__terms">
        <input name="termsAccepted" required type="checkbox" />
        <span>
          Ich stimme der Verarbeitung meiner Angaben zur Anmeldung zu und akzeptiere die{" "}
          <a href={`${legalBaseUrl}/impressum#teilnahme`}>Teilnahmebedingungen</a>. Hinweise in der{" "}
          <a href={`${legalBaseUrl}/datenschutz`}>Datenschutzerklärung</a>.
        </span>
      </label>

      {noticeMessage ? <p className="event-registration__notice">{noticeMessage}</p> : null}
      {errorMessage ? (
        <p className="event-registration__error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      <div className="event-registration__actions">
        <button disabled={isSubmitting} type="submit">
          {isSubmitting
            ? invitationCode.trim() && !codePreview?.valid
              ? "Code wird geprüft …"
              : "Wird verbindlich reserviert …"
            : invitationCode.trim() && !codePreview?.valid
              ? "Mit Code verbindlich anmelden"
              : paymentChoice === "online"
                ? "Verbindlich anmelden & weiter zur Zahlung"
                : contributionEnabled && contributionCents(contributionAmount) > 0
                  ? "Anmelden & Beitrag über Stripe geben"
                  : "Verbindlich anmelden"}
        </button>
        {showTrigger ? (
          <button type="button" onClick={() => setIsOpen(false)}>
            Zurück
          </button>
        ) : null}
      </div>
    </form>
  );
}
